import React, { useState } from 'react';
import { Modal, Table, Button, Form, DatePicker, InputNumber, Input, Space, Tabs } from 'antd';
import { HistoryOutlined, PlusOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
    useGetMaintenanceHistory,
    usePerformMaintenance,
    useUpdateMaintenanceDate
} from '../../../hooks/useEquipment';

const { TextArea } = Input;

const MaintenanceModal = ({ visible, onCancel, equipment }) => {
    const [activeTab, setActiveTab] = useState('history');
    const [form] = Form.useForm();
    const [dateForm] = Form.useForm();

    const { data: maintenanceHistory = [], isLoading } = useGetMaintenanceHistory(equipment?.id);
    const performMaintenanceMutation = usePerformMaintenance();
    const updateDateMutation = useUpdateMaintenanceDate();

    const handlePerformMaintenance = async (values) => {
        performMaintenanceMutation.mutate(
            {
                equipmentId: equipment.id,
                maintenanceDate: values.maintenanceDate.startOf('day').toISOString(),
                nextMaintenanceDate: values.nextMaintenanceDate.startOf('day').toISOString(),
                note: values.note,
                cost: values.cost
            },
            {
                onSuccess: () => {
                    form.resetFields();
                    setActiveTab('history');
                }
            }
        );
    };

    const handleUpdateDate = async (values) => {
        updateDateMutation.mutate(
            {
                equipmentId: equipment.id,
                newMaintenanceDate: values.newMaintenanceDate.startOf('day').toISOString()
            },
            {
                onSuccess: () => {
                    dateForm.resetFields();
                    setActiveTab('history');
                }
            }
        );
    };

    const columns = [
        {
            title: 'Maintenance Date',
            dataIndex: 'maintenanceDate',
            key: 'maintenanceDate',
            render: (date) => moment(date).format('DD/MM/YYYY'),
        },
        {
            title: 'Cost',
            dataIndex: 'cost',
            key: 'cost',
            render: (cost) => `EGP ${cost.toFixed(2)}`,
        },
        {
            title: 'Note',
            dataIndex: 'note',
            key: 'note',
            ellipsis: true,
        },
    ];

    const tabItems = [
        {
            key: 'history',
            label: (
                <span>
                    <HistoryOutlined /> Maintenance History
                </span>
            ),
            children: (
                <div>
                    <Table
                        columns={columns}
                        dataSource={maintenanceHistory}
                        loading={isLoading}
                        rowKey="id"
                        pagination={{ pageSize: 5 }}
                    />
                </div>
            ),
        },
        {
            key: 'perform',
            label: (
                <span>
                    <PlusOutlined /> Perform Maintenance
                </span>
            ),
            children: (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handlePerformMaintenance}
                    initialValues={{
                        cost: 0
                    }}
                >
                    <Form.Item
                        name="maintenanceDate"
                        label="Maintenance Date"
                        rules={[{ required: true, message: 'Please select the maintenance date' }]}
                    >
                         <DatePicker 
                            className="w-full"
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="nextMaintenanceDate"
                        label="Next Maintenance Date"
                        rules={[{ required: true, message: 'Please select the next maintenance date' }]}
                    >
                        <DatePicker 
                            className="w-full"
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="cost"
                        label="Cost"
                    >
                        <InputNumber
                            className="w-full"
                            min={0}
                            precision={2}
                            prefix="EGP"
                        />
                    </Form.Item>

                    <Form.Item
                        name="note"
                        label="Notes"
                        rules={[{ required: true, message: 'Please enter maintenance notes' }]}
                    >
                        <TextArea rows={4} placeholder="Enter maintenance details..." />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type=""
                                className='bg-primary hover:bg-primary_hover text-white'
                                htmlType="submit"
                                loading={performMaintenanceMutation.isLoading}
                            >
                                Save Maintenance
                            </Button>
                            <Button onClick={() => {
                                form.resetFields();
                                setActiveTab('history');
                            }}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            ),
        },
        // {
        //     key: 'updateDate',
        //     label: (
        //         <span>
        //             <CalendarOutlined /> Update Date
        //         </span>
        //     ),
        //     children: (
        //         <Form
        //             form={dateForm}
        //             layout="vertical"
        //             onFinish={handleUpdateDate}
        //         >
        //             <Form.Item
        //                 name="newMaintenanceDate"
        //                 label="New Maintenance Date"
        //                 rules={[{ required: true, message: 'Please select a date' }]}
        //             >
        //                 <DatePicker 
        //                     className="w-full"
        //                     format="DD/MM/YYYY"
        //                 />
        //             </Form.Item>
        //             <Form.Item>
        //                 <Space>
        //                     <Button
        //                     type=''
        //                         className='bg-primary hover:bg-primary_hover text-white'
        //                         htmlType="submit"
        //                         loading={updateDateMutation.isLoading}
        //                     >
        //                         Update Date
        //                     </Button>
        //                     <Button onClick={() => {
        //                         dateForm.resetFields();
        //                         setActiveTab('history');
        //                     }}>
        //                         Cancel
        //                     </Button>
        //                 </Space>
        //             </Form.Item>
        //         </Form>
        //     ),
        // },
    ];

    return (
        <Modal
            title={`Maintenance - ${equipment?.name || ''}`}
            open={visible}
            onCancel={onCancel}
            width={900}
            footer={null}
        >
            {equipment && (
                <>
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <Space size="large">
                            <div>
                                <strong>Code:</strong> {equipment.code}
                            </div>
                            <div>
                                <strong>Current Maintenance Date:</strong>{' '}
                                {equipment.mintanaceDate 
                                    ? moment(equipment.mintanaceDate).format('DD/MM/YYYY')
                                    : 'Not set'}
                            </div>
                            <div>
                                <strong>Reminder:</strong> {equipment.maintenanceReminderHours} hours
                            </div>
                        </Space>
                    </div>

                    <Tabs 
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                    />
                </>
            )}
        </Modal>
    );
};

export default MaintenanceModal;